import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './TransactionDashboard.css';

const TransactionDashboard = () => {
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    source: '',
    status: '',
    username: '',
    startDate: '',
    endDate: '',
    page: 1,
    limit: 20
  });
  const [totalPages, setTotalPages] = useState(1);
  const didLoadOnce = useRef(false);

  // Transactions: refetch when any filter changes, but debounce so typing in the
  // username box fires one request after the user pauses (not one per keystroke),
  // and abort any in-flight request so a slow older response can't overwrite a newer one.
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      fetchTransactions(controller.signal);
    }, 350);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.source, filters.status, filters.username, filters.startDate, filters.endDate, filters.page, filters.limit]);

  // Stats ignore the table filters, so fetch them once on mount only.
  useEffect(() => {
    fetchStats();
  }, []);

  const fetchTransactions = async (signal) => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      Object.keys(filters).forEach((key) => {
        if (filters[key]) params.append(key, filters[key]);
      });

      const response = await axios.get(`/api/transactions?${params}`, { signal });
      const body = response.data;

      if (body && Array.isArray(body.data) && body.pagination) {
        setTransactions(body.data);
        setTotalPages(body.pagination.pages || 1);
      } else if (Array.isArray(body)) {
        setTransactions(body);
        setTotalPages(1);
      } else {
        setTransactions([]);
        setTotalPages(1);
      }
      didLoadOnce.current = true;
    } catch (err) {
      if (axios.isCancel?.(err) || err.name === 'CanceledError') {
        return; // superseded by a newer request; leave state alone
      }
      setError('Failed to fetch transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const endDate = new Date().toISOString();
      const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const response = await axios.get(`/api/analytics/stats?startDate=${startDate}&endDate=${endDate}`);
      setStats(response.data);
    } catch (err) {
      // Stats are supplementary; a failure here should not break the table.
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const shortId = (id) => (id ? `${String(id).substring(0, 8)}...` : '-');

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed': return 'badge-success';
      case 'pending': return 'badge-warning';
      case 'processing': return 'badge-warning';
      case 'failed': return 'badge-danger';
      case 'expired': return 'badge-secondary';
      case 'perfect_panel_failed': return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  // Only the very first load shows the full-screen spinner. After that the UI stays
  // mounted (filters, table, pagination) and errors show inline, so a single failed
  // fetch can never strand the admin with no controls.
  if (loading && !didLoadOnce.current) {
    return <div className="loading">Loading transactions...</div>;
  }

  return (
    <div className="transaction-dashboard">
      <h2>Payment Transactions</h2>

      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Transactions</h3>
            <p className="stat-value">{stats.totalTransactions || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Total Revenue</h3>
            <p className="stat-value">RM {stats.totalAmount || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Success Rate</h3>
            <p className="stat-value">{stats.successRate || 0}%</p>
          </div>
          <div className="stat-card">
            <h3>Average Amount</h3>
            <p className="stat-value">RM {stats.avgAmount || 0}</p>
          </div>
        </div>
      )}

      {error && <div className="error" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="filters">
        <input
          type="text"
          name="username"
          placeholder="Search username..."
          value={filters.username}
          onChange={handleFilterChange}
          className="filter-input"
        />
        <select name="source" value={filters.source} onChange={handleFilterChange} className="filter-select">
          <option value="">All Sources</option>
          <option value="zhiborenqi.com">zhiborenqi.com</option>
          <option value="digitaladsconsultant.com">digitaladsconsultant.com</option>
        </select>
        <select name="status" value={filters.status} onChange={handleFilterChange} className="filter-select">
          <option value="">All Status</option>
          <option value="completed">Completed</option>
          <option value="processing">Processing</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
          <option value="expired">Expired</option>
          <option value="perfect_panel_failed">PP Failed</option>
        </select>
        <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} className="filter-input" />
        <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} className="filter-input" />
      </div>

      <div className="transactions-table">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Username</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Source</th>
              <th>Payment Method</th>
              <th>Xendit ID</th>
              <th>PP ID</th>
            </tr>
          </thead>
          <tbody>
            {transactions.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">No transactions found</td>
              </tr>
            ) : (
              transactions.map((transaction) => (
                <tr key={transaction._id}>
                  <td>{formatDate(transaction.createdAt)}</td>
                  <td>{transaction.username}</td>
                  <td>RM {transaction.amount}</td>
                  <td>
                    <span className={`badge ${getStatusBadgeClass(transaction.status)}`}>
                      {transaction.status}
                    </span>
                  </td>
                  <td>{transaction.source}</td>
                  <td>{transaction.paymentMethod || 'Credit Card'}</td>
                  <td className="small-text" title={transaction.xenditInvoiceId}>
                    {shortId(transaction.xenditInvoiceId)}
                  </td>
                  <td className="small-text">
                    {transaction.perfectPanelPaymentId || '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button onClick={() => handlePageChange(filters.page - 1)} disabled={filters.page === 1}>
            Previous
          </button>
          <span>Page {filters.page} of {totalPages}</span>
          <button onClick={() => handlePageChange(filters.page + 1)} disabled={filters.page === totalPages}>
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default TransactionDashboard;
